import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

import AlertNotice from "shared/AlertNotice";
import Button from "shared/Button";
import CopyBox from "shared/CopyBox";
import Loader from "shared/Loader";
import { Modal } from "shared/Modal";
import ModalActionBar from "shared/ModalActionBar";
import { ENVIRONMENT_CONFIG } from "shared/globals";

import { SupportAccountOperations } from "./SupportAccountOperations";
import ManualRepairOperations from "./ManualRepairOperations";
import { useDreamCast } from "./useDreamCast";
import styles from "./index.module.css";

const DREAMCAST_OTP_PATH = "/v1/dreamcast/user/accesscode";
const MANUAL_REPAIR_PERMISSION = "membership.dreamcast.manualrepair.create";
const OTP_VALUE_PATTERN = /^[A-Za-z0-9]{6}$/;
const MAX_TIMEOUT_DELAY_MS = 2147483647;

function normalizeEmail(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function createDefaultOtpState() {
  return {
    pending: false,
    code: null,
    expiresAt: null,
    error: null,
  };
}

function mapOtpErrorMessage(status) {
  if (status === 401 || status === 403) {
    return "You are not authorized to generate a login code.";
  }

  if (status === 404) {
    return "DreamCast account details are unavailable for this contact.";
  }

  if (status === 429) {
    return "Too many login code requests. Please try again in a moment.";
  }

  if (typeof status === "number" && status >= 500) {
    return "DreamCast login code service is unavailable right now.";
  }

  return "Unable to generate a login code right now.";
}

function parseOtpResponse(payload) {
  const code = typeof payload?.code === "string" ? payload.code : null;
  const expiresAt = payload?.expiresAt;
  const expiresAtDate = new Date(expiresAt);

  if (typeof code !== "string" || !OTP_VALUE_PATTERN.test(code)) {
    return null;
  }

  if (typeof expiresAt !== "string" || Number.isNaN(expiresAtDate.getTime())) {
    return null;
  }

  if (expiresAtDate.getTime() <= Date.now()) {
    return null;
  }

  return {
    code,
    expiresAt,
  };
}

function scheduleOtpExpiry(expiresAt, onExpire) {
  let cancelled = false;
  let timeoutId = null;

  const tick = () => {
    if (cancelled) {
      return;
    }

    const expiresAtTime = new Date(expiresAt).getTime();
    const remainingMs = expiresAtTime - Date.now();

    if (!Number.isFinite(expiresAtTime) || remainingMs <= 0) {
      onExpire();
      return;
    }

    timeoutId = setTimeout(tick, Math.min(remainingMs, MAX_TIMEOUT_DELAY_MS));
  };

  tick();

  return () => {
    cancelled = true;
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  };
}

const CONTACT_CONFIG = [
  {
    key: "primary",
    heading: "Primary Contact",
  },
  {
    key: "co",
    heading: "Co-Contact",
  },
];

const CONTACT_ROLES = new Set(CONTACT_CONFIG.map(({ key }) => key));
const DEPENDENCY_LABELS = {
  account: "Account",
  eligibility: "Eligibility",
  discrepancy: "Discrepancy",
  history: "History",
  freshness: "Freshness",
};
const DEPENDENCY_KEYS = Object.keys(DEPENDENCY_LABELS);

function formatTimestamp(value) {
  if (!value) {
    return "Unavailable";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unavailable";
  }

  return date.toLocaleString();
}

function toPositiveSafeInteger(value) {
  if (typeof value === "number" && Number.isSafeInteger(value) && value > 0) {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!/^[1-9][0-9]*$/.test(trimmed)) {
    return null;
  }

  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed)) {
    return null;
  }

  return parsed;
}

function isDependencyAuthoritative(dependency) {
  return dependency?.status === "ok";
}

function formatDependencyStatus(dependency) {
  if (!dependency?.status) {
    return "Unavailable";
  }

  if (dependency.status === "ok") {
    return "Available";
  }

  if (dependency.status === "stale") {
    return "Stale";
  }

  return "Unavailable";
}

function getDependencyMessage(dependency, fallbackMessage) {
  if (dependency?.message) {
    return dependency.message;
  }
  return fallbackMessage;
}

function buildDependencyNotices(partialFailures, dependenciesByRole = {}) {
  const notices = [];
  const pushNotice = (message) => {
    if (!message || notices.includes(message)) {
      return;
    }
    notices.push(message);
  };

  partialFailures.forEach((failure) => pushNotice(failure));

  CONTACT_CONFIG.forEach(({ key, heading }) => {
    const dependencies = dependenciesByRole?.[key] || {};
    DEPENDENCY_KEYS.forEach((dependencyKey) => {
      const dependency = dependencies[dependencyKey];
      if (dependency?.status === "ok") {
        return;
      }

      pushNotice(
        getDependencyMessage(
          dependency,
          `${heading} ${DEPENDENCY_LABELS[dependencyKey]} dependency is ${
            dependency?.status || "unavailable"
          }.`
        )
      );
    });
  });

  return notices;
}

function ContactPanel({
  contactKey,
  heading,
  contact,
  dependencies,
  historyPagination,
  canIssueOtp,
  onOpenOtpModal,
  iboNumber,
  hasPermission,
  makeAuthedRequest,
  supportsManualRepair,
}) {
  if (!contact) {
    const accountDependency = dependencies?.account;
    const accountIsAuthoritative = isDependencyAuthoritative(accountDependency);

    if (accountIsAuthoritative) {
      return (
        <section
          className={styles.contactSection}
          aria-label={`${heading} DreamCast`}
        >
          <header className={styles.contactHeader}>
            <h3 className={styles.contactHeading}>{heading}</h3>
          </header>
          <AlertNotice title="No DreamCast account found">
            <div>{heading} does not currently have DreamCast account data.</div>
          </AlertNotice>
        </section>
      );
    }

    const accountDependencyStatus = accountDependency?.status || "unknown";

    return (
      <section
        className={styles.contactSection}
        aria-label={`${heading} DreamCast`}
      >
        <header className={styles.contactHeader}>
          <h3 className={styles.contactHeading}>{heading}</h3>
        </header>
        <AlertNotice title="DreamCast account availability unknown">
          <div>
            {heading} DreamCast account status is {accountDependencyStatus}.
          </div>
          <div>
            {getDependencyMessage(
              accountDependency,
              "DreamCast account authority is currently unavailable."
            )}
          </div>
        </AlertNotice>
      </section>
    );
  }

  const accountDependency = dependencies.account;
  const eligibilityDependency = dependencies.eligibility;
  const discrepancyDependency = dependencies.discrepancy;
  const historyDependency = dependencies.history;
  const freshnessDependency = dependencies.freshness;

  const accountIsAuthoritative = isDependencyAuthoritative(accountDependency);
  const eligibilityIsAuthoritative = isDependencyAuthoritative(
    eligibilityDependency
  );
  const discrepancyIsAuthoritative = isDependencyAuthoritative(
    discrepancyDependency
  );
  const historyIsAuthoritative = isDependencyAuthoritative(historyDependency);
  const freshnessIsAuthoritative =
    isDependencyAuthoritative(freshnessDependency);

  const historyEntries = Array.isArray(contact.history) ? contact.history : [];
  const historyPaginationByRole = historyPagination?.[contactKey] || {
    returnedCount: historyEntries.length,
    truncated: false,
  };
  const hasContactEmail =
    typeof contact.email === "string" && contact.email.trim();
  const canGenerateLoginCode =
    Boolean(canIssueOtp) &&
    accountIsAuthoritative &&
    contact.account.status === "available" &&
    Boolean(hasContactEmail);
  const contactWwgId = toPositiveSafeInteger(contact.wwgId);
  const canRepairContact =
    Boolean(supportsManualRepair) &&
    typeof hasPermission === "function" &&
    hasPermission(MANUAL_REPAIR_PERMISSION) &&
    accountIsAuthoritative &&
    String(contact.iboNumber || "").trim() === String(iboNumber || "").trim() &&
    Number.isSafeInteger(contactWwgId);

  return (
    <section
      className={styles.contactSection}
      aria-label={`${heading} DreamCast`}
    >
      <header className={styles.contactHeader}>
        <h3 className={styles.contactHeading}>{heading}</h3>
        <div className={styles.contactActions}>
          {canGenerateLoginCode && (
            <Button
              size="small"
              className={styles.generateCodeButton}
              onClick={() => onOpenOtpModal(contactKey)}
              aria-label={`Generate login code for ${heading}`}
            >
              Generate login code
            </Button>
          )}
          <SupportAccountOperations
            contactKey={contactKey}
            heading={heading}
            contact={contact}
            iboNumber={iboNumber}
            accountIsAuthoritative={accountIsAuthoritative}
            hasPermission={hasPermission}
            makeAuthedRequest={makeAuthedRequest}
          />
          <ManualRepairOperations
            target={{
              mode: "contact",
              heading,
              displayName: contact.displayName,
              wwgId: contactWwgId,
              iboNumber,
              identityToken: `${iboNumber}|${contactKey}|${contact.role}|${contact.wwgId}|${contact.email}`,
            }}
            canRepair={canRepairContact}
            makeAuthedRequest={makeAuthedRequest}
          />
        </div>
      </header>

      <div className={styles.surfaceGrid}>
        <article className={styles.surfaceCard}>
          <h4>Account</h4>
          <dl className={styles.surfaceList}>
            <dt>Name</dt>
            <dd>{contact.displayName}</dd>
            <dt>Email</dt>
            <dd>{contact.email}</dd>
            <dt>WWG ID</dt>
            <dd>{contact.wwgId}</dd>
            <dt>DreamCast ID</dt>
            <dd>
              {accountIsAuthoritative
                ? contact.account.dreamCastId || "Unavailable"
                : "Unavailable"}
            </dd>
            <dt>Status</dt>
            <dd
              className={classNames({
                [styles.ok]:
                  accountIsAuthoritative &&
                  contact.account.status === "available",
                [styles.pending]:
                  accountIsAuthoritative &&
                  contact.account.status === "pending",
                [styles.unavailable]:
                  accountIsAuthoritative &&
                  contact.account.status === "unavailable",
                [styles.stale]: !accountIsAuthoritative,
              })}
            >
              {accountIsAuthoritative
                ? contact.account.status === "available"
                  ? "Available"
                  : contact.account.status === "pending"
                  ? "Pending"
                  : "Unavailable"
                : formatDependencyStatus(accountDependency)}
            </dd>
            <dt>Last Synced</dt>
            <dd>
              {accountIsAuthoritative && freshnessIsAuthoritative
                ? formatTimestamp(contact.account.lastSyncedAt)
                : "Unavailable"}
            </dd>
          </dl>
          {!accountIsAuthoritative && (
            <p className={styles.dependencyUnavailable}>
              {getDependencyMessage(
                accountDependency,
                "DreamCast account details are currently unavailable."
              )}
            </p>
          )}
          {!freshnessIsAuthoritative && (
            <p className={styles.dependencyUnavailable}>
              {getDependencyMessage(
                freshnessDependency,
                "Freshness details are currently unavailable."
              )}
            </p>
          )}
        </article>

        <article className={styles.surfaceCard}>
          <h4>Eligibility</h4>
          {eligibilityIsAuthoritative ? (
            <dl className={styles.surfaceList}>
              <dt>Eligible</dt>
              <dd>{contact.eligibility.isEligible ? "Yes" : "No"}</dd>
              <dt>Reason</dt>
              <dd>{contact.eligibility.reason}</dd>
            </dl>
          ) : (
            <p className={styles.dependencyUnavailable}>
              {getDependencyMessage(
                eligibilityDependency,
                "Eligibility details are currently unavailable."
              )}
            </p>
          )}
        </article>

        <article className={styles.surfaceCard}>
          <h4>Discrepancy</h4>
          {discrepancyIsAuthoritative && contact.discrepancy == null ? (
            <p>Unavailable.</p>
          ) : discrepancyIsAuthoritative &&
            contact.discrepancy.hasDiscrepancy ? (
            <ul className={styles.discrepancyList}>
              {contact.discrepancy.details.map((detail) => (
                <li key={`${contactKey}-${detail}`}>{detail}</li>
              ))}
            </ul>
          ) : discrepancyIsAuthoritative ? (
            <p>None reported.</p>
          ) : (
            <p className={styles.dependencyUnavailable}>
              {getDependencyMessage(
                discrepancyDependency,
                "Discrepancy details are currently unavailable."
              )}
            </p>
          )}
        </article>
      </div>

      <div className={styles.historySection}>
        <h4>History</h4>
        {!historyIsAuthoritative ? (
          <p className={styles.dependencyUnavailable}>
            {getDependencyMessage(
              historyDependency,
              "DreamCast history is currently unavailable."
            )}
          </p>
        ) : historyEntries.length === 0 ? (
          <p className={styles.emptyHistory}>No DreamCast history available.</p>
        ) : (
          <table className={styles.historyTable}>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Event</th>
              </tr>
            </thead>
            <tbody>
              {historyEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatTimestamp(entry.happenedAt)}</td>
                  <td>
                    <div>{entry.event}</div>
                    <div className={styles.historyMeta}>
                      Source: {entry.source}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {historyIsAuthoritative && historyPaginationByRole.truncated && (
          <p className={styles.historyTruncated}>
            Showing the most recent {historyPaginationByRole.returnedCount}{" "}
            events.
          </p>
        )}
      </div>
    </section>
  );
}

export function DreamCastContent({
  iboNumber,
  scenarioKey,
  dataProvider,
  hasPermission,
  makeAuthedRequest,
}) {
  const [otpModalContactKey, setOtpModalContactKey] = useState(null);
  const [otpState, setOtpState] = useState(createDefaultOtpState);
  const isMountedRef = useRef(true);
  const otpRequestGenerationRef = useRef(0);
  const otpContextRef = useRef({
    otpModalContactKey: null,
    contactsByRole: {
      primary: null,
      co: null,
    },
  });

  const { data, loading, error } = useDreamCast(iboNumber, {
    scenarioKey,
    dataProvider,
    makeAuthedRequest,
  });

  const contactsByRole = useMemo(() => {
    const byRole = {
      primary: null,
      co: null,
    };

    [data?.contacts?.primary, data?.contacts?.co].forEach((contact) => {
      if (contact && CONTACT_ROLES.has(contact.role)) {
        byRole[contact.role] = contact;
      }
    });

    return byRole;
  }, [data]);

  const dependenciesByRole = useMemo(
    () => data?.dependencies || {},
    [data?.dependencies]
  );
  const historyPagination = useMemo(
    () => data?.historyPagination || {},
    [data?.historyPagination]
  );

  const canIssueOtp =
    Boolean(ENVIRONMENT_CONFIG?.flags?.supportsDreamCastOtpIssuance) &&
    typeof hasPermission === "function" &&
    hasPermission("membership.dreamcast.accesscode.create");
  const supportsManualRepair = Boolean(
    ENVIRONMENT_CONFIG?.flags?.supportsDreamCastManualRepair
  );

  const primaryContact = contactsByRole.primary;
  const coContact = contactsByRole.co;
  const canRepairBoth =
    supportsManualRepair &&
    typeof hasPermission === "function" &&
    hasPermission(MANUAL_REPAIR_PERMISSION) &&
    Boolean(primaryContact) &&
    Boolean(coContact) &&
    isDependencyAuthoritative(dependenciesByRole?.primary?.account) &&
    isDependencyAuthoritative(dependenciesByRole?.co?.account) &&
    String(primaryContact?.iboNumber || "").trim() ===
      String(iboNumber || "").trim() &&
    String(coContact?.iboNumber || "").trim() ===
      String(iboNumber || "").trim() &&
    Number.isSafeInteger(toPositiveSafeInteger(primaryContact?.wwgId)) &&
    Number.isSafeInteger(toPositiveSafeInteger(coContact?.wwgId));

  const clearOtpState = useCallback(() => {
    if (isMountedRef.current) {
      setOtpState(createDefaultOtpState());
    }
  }, []);

  const invalidateOtpGeneration = useCallback(
    ({ clearState = true } = {}) => {
      otpRequestGenerationRef.current += 1;
      if (clearState) {
        clearOtpState();
      }
    },
    [clearOtpState]
  );

  const buildContactIdentity = (contactKey, contact) => ({
    contactKey,
    role: CONTACT_ROLES.has(contact?.role) ? contact.role : "",
    email: normalizeEmail(contact?.email),
  });

  const isContactEligibleForOtpIssuance = useCallback(
    (contactKey, contact, allowIssueOtp = canIssueOtp) => {
      if (!allowIssueOtp || !contact || !CONTACT_ROLES.has(contactKey)) {
        return false;
      }

      const dependency = dependenciesByRole?.[contactKey]?.account;
      const hasAccountAuthority = isDependencyAuthoritative(dependency);
      const hasAvailableAccount = contact.account?.status === "available";
      const hasContactEmail = Boolean(normalizeEmail(contact.email));

      return hasAccountAuthority && hasAvailableAccount && hasContactEmail;
    },
    [canIssueOtp, dependenciesByRole]
  );

  const isOtpRequestCurrent = (generation, requestIdentity) => {
    if (!isMountedRef.current) {
      return false;
    }

    if (otpRequestGenerationRef.current !== generation) {
      return false;
    }

    const latestContext = otpContextRef.current;
    if (latestContext.otpModalContactKey !== requestIdentity.contactKey) {
      return false;
    }

    if (!latestContext.canIssueOtp) {
      return false;
    }

    const currentContact =
      latestContext.contactsByRole[requestIdentity.contactKey];
    if (!currentContact) {
      return false;
    }

    const currentIdentity = buildContactIdentity(
      requestIdentity.contactKey,
      currentContact
    );

    return (
      currentIdentity.role === requestIdentity.role &&
      isContactEligibleForOtpIssuance(
        requestIdentity.contactKey,
        currentContact,
        latestContext.canIssueOtp
      ) &&
      currentIdentity.email === requestIdentity.email
    );
  };

  useEffect(() => {
    otpContextRef.current = {
      otpModalContactKey,
      contactsByRole,
      dependenciesByRole,
      canIssueOtp,
    };
  }, [otpModalContactKey, contactsByRole, dependenciesByRole, canIssueOtp]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      invalidateOtpGeneration({ clearState: false });
    };
  }, [invalidateOtpGeneration]);

  const closeOtpModal = useCallback(() => {
    invalidateOtpGeneration();
    setOtpModalContactKey(null);
  }, [invalidateOtpGeneration]);

  const openOtpModal = (contactKey) => {
    invalidateOtpGeneration();
    setOtpModalContactKey(contactKey);
  };

  const modalContact = otpModalContactKey
    ? contactsByRole[otpModalContactKey]
    : null;
  const modalHeading =
    CONTACT_CONFIG.find(({ key }) => key === otpModalContactKey)?.heading ||
    "Selected Contact";

  const modalContactIdentity = otpModalContactKey
    ? buildContactIdentity(otpModalContactKey, modalContact)
    : null;

  useEffect(() => {
    closeOtpModal();
  }, [
    closeOtpModal,
    iboNumber,
    contactsByRole.primary?.role,
    contactsByRole.primary?.email,
    contactsByRole.co?.role,
    contactsByRole.co?.email,
  ]);

  useEffect(() => {
    if (!otpModalContactKey) {
      return;
    }

    if (
      !isContactEligibleForOtpIssuance(
        otpModalContactKey,
        modalContact,
        canIssueOtp
      )
    ) {
      closeOtpModal();
    }
  }, [
    canIssueOtp,
    closeOtpModal,
    isContactEligibleForOtpIssuance,
    modalContact,
    modalContactIdentity?.email,
    modalContactIdentity?.role,
    otpModalContactKey,
  ]);

  useEffect(() => {
    if (!otpState.code || !otpState.expiresAt) {
      return;
    }

    const trackedExpiry = otpState.expiresAt;

    return scheduleOtpExpiry(trackedExpiry, () => {
      if (!isMountedRef.current) {
        return;
      }

      setOtpState((current) => {
        if (!current.code || current.expiresAt !== trackedExpiry) {
          return current;
        }

        return createDefaultOtpState();
      });
    });
  }, [otpState.code, otpState.expiresAt]);

  const handleConfirmOtpIssuance = async () => {
    if (otpState.pending || !otpModalContactKey || !modalContact) {
      return;
    }

    const requestIdentity = buildContactIdentity(
      otpModalContactKey,
      modalContact
    );
    if (!requestIdentity.role || !requestIdentity.email) {
      setOtpState({
        ...createDefaultOtpState(),
        error:
          "This contact is not currently eligible for login code generation.",
      });
      return;
    }

    if (!isContactEligibleForOtpIssuance(otpModalContactKey, modalContact)) {
      setOtpState({
        ...createDefaultOtpState(),
        error:
          "This contact is not currently eligible for login code generation.",
      });
      return;
    }

    const requestGeneration = otpRequestGenerationRef.current + 1;
    otpRequestGenerationRef.current = requestGeneration;

    setOtpState({
      ...createDefaultOtpState(),
      pending: true,
    });

    try {
      const response = await makeAuthedRequest(DREAMCAST_OTP_PATH, {
        method: "PUT",
        body: JSON.stringify({
          email: requestIdentity.email,
        }),
      });

      if (!isOtpRequestCurrent(requestGeneration, requestIdentity)) {
        return;
      }

      if (!response.ok) {
        setOtpState({
          ...createDefaultOtpState(),
          error: mapOtpErrorMessage(response.status),
        });
        return;
      }

      let payload = null;
      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }

      if (!isOtpRequestCurrent(requestGeneration, requestIdentity)) {
        return;
      }

      const parsed = parseOtpResponse(payload);
      if (!parsed) {
        setOtpState({
          ...createDefaultOtpState(),
          error: "Received an invalid login code response. Please retry.",
        });
        return;
      }

      setOtpState({
        pending: false,
        code: parsed.code,
        expiresAt: parsed.expiresAt,
        error: null,
      });
    } catch (error) {
      if (!isOtpRequestCurrent(requestGeneration, requestIdentity)) {
        return;
      }

      setOtpState({
        ...createDefaultOtpState(),
        error: "DreamCast login code service is unavailable right now.",
      });
    }
  };

  if (loading) {
    return (
      <div>
        <p aria-live="polite">Loading DreamCast data...</p>
        <Loader />
      </div>
    );
  }

  if (error) {
    if (error.code === "DREAMCAST_NOT_CONNECTED") {
      return (
        <AlertNotice title="DreamCast integration unavailable">
          <div>
            DreamCast is not connected for this environment. Fixture data is
            only available through explicit Storybook or test provider
            configuration.
          </div>
        </AlertNotice>
      );
    }

    return (
      <AlertNotice title="DreamCast data unavailable">
        <div>{error.message || "Unable to load DreamCast data."}</div>
      </AlertNotice>
    );
  }

  if (!data) {
    return (
      <AlertNotice title="No DreamCast data available">
        <div>DreamCast account information is not available for this IBO.</div>
      </AlertNotice>
    );
  }

  const noPrimaryContact = !contactsByRole.primary;
  const noCoContact = !contactsByRole.co;
  const allAccountDependenciesAuthoritative = CONTACT_CONFIG.every(({ key }) =>
    isDependencyAuthoritative(dependenciesByRole?.[key]?.account)
  );

  if (noPrimaryContact && noCoContact && allAccountDependenciesAuthoritative) {
    return (
      <AlertNotice title="No DreamCast data available">
        <div>DreamCast account information is not available for this IBO.</div>
      </AlertNotice>
    );
  }

  const dependencyNotices = buildDependencyNotices(
    data.partialFailures,
    dependenciesByRole
  );

  return (
    <div className={styles.container}>
      <section className={styles.freshness} aria-label="DreamCast freshness">
        <strong>DreamCast freshness:</strong>{" "}
        <ul>
          {CONTACT_CONFIG.map(({ key, heading }) => {
            const contact = contactsByRole[key];
            const freshnessDependency = dependenciesByRole?.[key]?.freshness;
            const freshnessIsAuthoritative =
              isDependencyAuthoritative(freshnessDependency);

            return (
              <li key={key}>
                <span>{heading}: </span>
                <span>
                  {contact && freshnessIsAuthoritative
                    ? formatTimestamp(contact.account.lastSyncedAt)
                    : "Unavailable"}
                </span>
                {!freshnessIsAuthoritative && (
                  <div className={styles.dependencyUnavailable}>
                    {getDependencyMessage(
                      freshnessDependency,
                      "Freshness details are currently unavailable."
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <div className={styles.iboActions}>
        <ManualRepairOperations
          target={{
            mode: "both",
            heading: "Both Contacts",
            displayName: "",
            iboNumber,
            identityToken: `${iboNumber}|both|${primaryContact?.wwgId || ""}|${
              coContact?.wwgId || ""
            }`,
          }}
          canRepair={canRepairBoth}
          makeAuthedRequest={makeAuthedRequest}
        />
      </div>

      {dependencyNotices.length > 0 && (
        <AlertNotice title="DreamCast partial dependency failure">
          <ul className={styles.noticeList}>
            {dependencyNotices.map((failure, index) => (
              <li key={`${failure}-${index}`}>{failure}</li>
            ))}
          </ul>
        </AlertNotice>
      )}

      {CONTACT_CONFIG.map(({ key, heading }) => (
        <ContactPanel
          key={key}
          contactKey={key}
          heading={heading}
          contact={contactsByRole[key]}
          dependencies={dependenciesByRole[key] || {}}
          historyPagination={historyPagination}
          canIssueOtp={canIssueOtp}
          onOpenOtpModal={openOtpModal}
          iboNumber={iboNumber}
          hasPermission={hasPermission}
          makeAuthedRequest={makeAuthedRequest}
          supportsManualRepair={supportsManualRepair}
        />
      ))}

      <Modal
        title="Generate login code"
        isOpen={Boolean(otpModalContactKey)}
        onClose={closeOtpModal}
      >
        <div className={styles.otpModalContent}>
          <p className={styles.otpModalText}>
            Generate a DreamCast login code for {modalHeading}
            {modalContact?.displayName ? ` (${modalContact.displayName})` : ""}?
          </p>
          <p className={styles.otpModalText}>
            Email: <strong>{modalContact?.email || "Unavailable"}</strong>
          </p>

          {otpState.pending && (
            <p role="status" aria-live="polite" className={styles.otpStatus}>
              Generating login code...
            </p>
          )}

          {otpState.error && (
            <AlertNotice title="Unable to generate login code">
              <div>{otpState.error}</div>
            </AlertNotice>
          )}

          {otpState.code && otpState.expiresAt && (
            <div className={styles.otpResult}>
              <CopyBox label="Login code" value={otpState.code} />
              <p className={styles.otpExpiry}>
                Expires at: {formatTimestamp(otpState.expiresAt)}
              </p>
            </div>
          )}

          <ModalActionBar>
            <Button onClick={closeOtpModal}>Close</Button>
            <Button
              onClick={handleConfirmOtpIssuance}
              disabled={otpState.pending}
            >
              {otpState.error ? "Retry" : "Generate login code"}
            </Button>
          </ModalActionBar>
        </div>
      </Modal>
    </div>
  );
}
